import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  EyeIcon,
  PencilSquareIcon,
  PlayIcon,
  PlusIcon,
  PauseIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import WhatsAppPanelShell from './WhatsAppPanelShell';
import { inputClass } from './whatsappCampaignFormUtils';
import { StatusBadge } from './whatsappCampaignUi';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { WHATSAPP_BASE_PATH } from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppCampaign } from '../../models/MasterAdmin/whatsappCampaign';
import {
  CAMPAIGN_TYPE_LABELS,
  SEND_MODE_LABELS,
  SERVICE_VERTICAL_LABELS,
} from '../../models/MasterAdmin/whatsappCampaign';
import {
  activateWhatsAppCampaign,
  deleteWhatsAppCampaign,
  fetchWhatsAppCampaigns,
  pauseWhatsAppCampaign,
} from '../../services/MasterAdmin/whatsappCampaignService';

const WhatsAppCampaignsPanel: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<WhatsAppCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const campRes = await fetchWhatsAppCampaigns({ search: search || undefined, limit: 50 });
      setCampaigns(campRes.data || []);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load campaigns'));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleActivate = async (id: string) => {
    try {
      await activateWhatsAppCampaign(id);
      toast.success('Campaign activated — registered in Interakt API campaigns for analytics');
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Activate failed'));
    }
  };

  const handlePause = async (id: string) => {
    try {
      await pauseWhatsAppCampaign(id);
      toast.success('Campaign paused');
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Pause failed'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await deleteWhatsAppCampaign(id);
      toast.success('Deleted');
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Delete failed'));
    }
  };

  return (
    <WhatsAppPanelShell
      title="Campaigns"
      description="Broadcast messages or automated event campaigns. Activating or sending registers an Interakt API campaign for delivery analytics."
      actions={
        <>
          <button
            type="button"
            disabled={loading}
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm dark:border-gray-600"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => navigate(`${WHATSAPP_BASE_PATH}/campaigns/new`)}
            className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1da851]"
          >
            <PlusIcon className="h-4 w-4" />
            New campaign
          </button>
        </>
      }
    >
      <div className="mb-4 max-w-md">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search campaigns..."
          className={inputClass}
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/80">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Campaign</th>
              <th className="px-4 py-2 text-left font-medium">Type</th>
              <th className="px-4 py-2 text-left font-medium">Service</th>
              <th className="px-4 py-2 text-left font-medium">Status</th>
              <th className="px-4 py-2 text-left font-medium">Sent</th>
              <th className="px-4 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : campaigns.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No campaigns yet
                </td>
              </tr>
            ) : (
              campaigns.map((c) => (
                <tr
                  key={c.id}
                  className="cursor-pointer border-t border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                  onClick={() => navigate(`${WHATSAPP_BASE_PATH}/campaigns/${c.id}`)}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white">{c.name}</p>
                    <p className="text-xs text-gray-500">{c.templateName}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p>{CAMPAIGN_TYPE_LABELS[c.campaignType || 'BROADCAST']}</p>
                    <p className="text-xs text-gray-500">{SEND_MODE_LABELS[c.sendMode || 'MANUAL']}</p>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {c.serviceVertical ? SERVICE_VERTICAL_LABELS[c.serviceVertical] || c.serviceVertical : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {c.campaignType === 'AUTOMATED' ? (
                      <StatusBadge value={c.automationStatus || 'DRAFT'} />
                    ) : (
                      <StatusBadge value={c.status} />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {c.campaignType === 'AUTOMATED'
                      ? `${c.automationStats?.jobsSent ?? c.totalSent ?? 0} jobs`
                      : `${c.totalSent}/${c.recipientCount}`}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => navigate(`${WHATSAPP_BASE_PATH}/campaigns/${c.id}`)}
                        className="rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800"
                        title="View"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`${WHATSAPP_BASE_PATH}/campaigns/${c.id}/edit`)}
                        className="rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800"
                        title="Edit"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      {c.campaignType === 'AUTOMATED' && c.automationStatus !== 'ACTIVE' && (
                        <button
                          type="button"
                          onClick={() => void handleActivate(c.id)}
                          className="rounded p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30"
                          title="Activate"
                        >
                          <PlayIcon className="h-4 w-4" />
                        </button>
                      )}
                      {c.campaignType === 'AUTOMATED' && c.automationStatus === 'ACTIVE' && (
                        <button
                          type="button"
                          onClick={() => void handlePause(c.id)}
                          className="rounded p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                          title="Pause"
                        >
                          <PauseIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void handleDelete(c.id)}
                        className="rounded p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </WhatsAppPanelShell>
  );
};

export default WhatsAppCampaignsPanel;
